/**
 * Encoding rules for Enhanced Response Types (ERT) data-collection
 * values. Every data-collection answer travels as a single STRING on
 * `subResponses[].responseValue` — including NUMERIC ("12.5", never
 * 12.5) and MULTI_SELECT (comma-joined) — so these helpers own the
 * parse / serialize / validate rules in one pure, testable place.
 *
 * Pure — no react, no side effects.
 */

/**
 * Character cap on TEXT responses, matching V1 desktop. Over-length
 * input is REFUSED rather than truncated: truncation silently discards
 * the tail of a paste, which reads to the auditor as data loss.
 */
export const TEXT_MAX_LENGTH = 100;

/**
 * Accepts empty, an optional leading minus, digits, and at most one
 * decimal point. Deliberately permissive about precision: V1 carries
 * no min / max / decimal-places configuration, so a stricter client
 * rule here would reject values the desktop app accepts for the very
 * same question.
 */
const NUMERIC_PATTERN = /^-?\d*\.?\d*$/;

/**
 * Separator for MULTI_SELECT values. Fixed by wire compatibility: V1
 * desktop, the audit summary table, and the CSV export all read the
 * field as comma-joined, so an option label containing a comma
 * corrupts the round-trip. That is a pre-existing V1 constraint
 * carried forward deliberately rather than diverging the two clients'
 * encodings.
 */
const MULTI_SEPARATOR = ',';

/** Reference-stable empty result, so callers can memoise on it. */
const NO_VALUES: readonly string[] = [];

/** True when the text is within the TEXT response cap. */
export function isWithinTextLimit(value: string): boolean {
  return value.length <= TEXT_MAX_LENGTH;
}

/**
 * True when the text is a legal partial numeric entry. Called per
 * keystroke, so it must accept in-progress input like `-` and `0.`
 * that isn't yet a complete number.
 */
export function isNumericEntry(value: string): boolean {
  return NUMERIC_PATTERN.test(value);
}

/** Splits a comma-joined MULTI_SELECT value into its selected options. */
export function parseMultiValue(value: string): readonly string[] {
  if (value === '') return NO_VALUES;
  return value.split(MULTI_SEPARATOR);
}

/**
 * Adds or removes one option from a MULTI_SELECT value, returning the
 * new wire string.
 *
 * The result is ordered by the question's `options` list, NOT by the
 * order the auditor tapped. Two auditors who select the same options
 * in different orders must produce the same stored string, otherwise
 * identical answers compare unequal and every re-render of a reordered
 * value looks like a change worth saving.
 *
 * Unknown options in the incoming value are dropped, since the result
 * is rebuilt from `options`. An empty result means "cleared", which
 * the payload builder turns into an omitted field.
 */
export function toggleMultiValue(
  options: readonly string[],
  currentValue: string,
  option: string,
  isChecked: boolean,
): string {
  const selected = parseMultiValue(currentValue);
  const next = options.filter((candidate) =>
    candidate === option ? isChecked : selected.includes(candidate),
  );
  return next.join(MULTI_SEPARATOR);
}
